// frontend/src/services/formationService.js

const API_URL = process.env.REACT_APP_API_BASE_URL || 'https://douane.vtymconsultinggroup.com/wp-json';

/**
 * Service pour gérer les formations
 */
export const formationService = {

    /**
     * Obtenir toutes les formations
     * @param {Object} filters - Filtres optionnels (type, niveau)
     */
    async getAllFormations(filters = {}) {
        try {
            let url = `${API_URL}/douane/v1/formations`;

            // Ajouter les paramètres de filtrage
            const params = new URLSearchParams();
            if (filters.type) params.append('type', filters.type);
            if (filters.niveau) params.append('niveau', filters.niveau);
            if (filters.per_page) params.append('per_page', filters.per_page);

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const data = await response.json();

            // Vérifier que les formations ont bien les prix
            if (data.success && data.formations) {
                data.formations = data.formations.map(formation => {
                    // S'assurer que les prix sont des nombres
                    return {
                        ...formation,
                        prix_particulier: parseInt(formation.prix_particulier) || 0,
                        prix_entreprise: parseInt(formation.prix_entreprise) || 0,
                        places_disponibles: parseInt(formation.places_disponibles) || 0,
                        inscriptions_count: parseInt(formation.inscriptions_count) || 0
                    };
                });
            }

            return data;

        } catch (error) {
            console.error('Erreur lors de la récupération des formations:', error);
            throw error;
        }
    },

    /**
     * Obtenir une formation spécifique
     * @param {number} id - ID de la formation
     */
    async getFormation(id) {
        try {
            const response = await fetch(`${API_URL}/douane/v1/formations/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Formation non trouvée: ${response.status}`);
            }

            const data = await response.json();

            // S'assurer que les prix sont des nombres
            if (data.success && data.formation) {
                data.formation = {
                    ...data.formation,
                    prix_particulier: parseInt(data.formation.prix_particulier) || 0,
                    prix_entreprise: parseInt(data.formation.prix_entreprise) || 0,
                    places_disponibles: parseInt(data.formation.places_disponibles) || 0,
                    inscriptions_count: parseInt(data.formation.inscriptions_count) || 0
                };
            }

            return data;

        } catch (error) {
            console.error('Erreur lors de la récupération de la formation:', error);
            throw error;
        }
    },

    /**
     * Obtenir le prix d'une formation
     * @param {number} id - ID de la formation
     * @param {string} type - Type de prix ('particulier' ou 'entreprise')
     */
    async getFormationPrix(id, type = 'particulier') {
        try {
            const response = await fetch(
                `${API_URL}/douane/v1/formations/${id}/prix?type=${type}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Erreur: ${response.status}`);
            }

            const data = await response.json();

            // S'assurer que les prix sont des nombres
            if (data.success) {
                data.prix.particulier = parseInt(data.prix.particulier) || 0;
                data.prix.entreprise = parseInt(data.prix.entreprise) || 0;
                data.prix_selectionne = parseInt(data.prix_selectionne) || 0;
                data.places_disponibles = parseInt(data.places_disponibles) || 0;
            }

            return data;

        } catch (error) {
            console.error('Erreur lors de la récupération du prix:', error);
            throw error;
        }
    },

    /**
     * Créer une inscription
     * @param {Object} inscriptionData - Données de l'inscription
     */
    async createInscription(inscriptionData) {
        try {
            const response = await fetch(`${API_URL}/douane/v1/inscriptions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(inscriptionData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Erreur lors de l\'inscription');
            }

            return await response.json();

        } catch (error) {
            console.error('Erreur lors de la création de l\'inscription:', error);
            throw error;
        }
    },

    /**
     * Obtenir les statistiques d'une formation (admin seulement)
     * @param {number} id - ID de la formation
     * @param {string} token - Token JWT admin
     */
    async getFormationStats(id, token) {
        try {
            const response = await fetch(
                `${API_URL}/douane/v1/formations/${id}/stats`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Erreur: ${response.status}`);
            }

            return await response.json();

        } catch (error) {
            console.error('Erreur lors de la récupération des stats:', error);
            throw error;
        }
    }
};

/**
 * Fonction utilitaire pour formater les prix
 */
export const formatPrix = (prix) => {
    if (!prix || prix === 0 || prix === '0') {
        return 'Gratuit';
    }
    return `${parseInt(prix).toLocaleString('fr-FR')} FCFA`;
};

/**
 * Fonction utilitaire pour obtenir le label du niveau
 */
export const getNiveauLabel = (niveau) => {
    const niveaux = {
        'debutant': 'Débutant',
        'intermediaire': 'Intermédiaire',
        'avance': 'Avancé',
        'tous_niveaux': 'Tous niveaux'
    };
    return niveaux[niveau] || 'Non spécifié';
};

/**
 * Fonction utilitaire pour obtenir le label du type
 */
export const getTypeLabel = (type) => {
    const types = {
        'presentiel': 'Présentiel',
        'ligne': 'En ligne',
        'hybride': 'Hybride'
    };
    return types[type] || type;
};

export default formationService;